import { stringify } from "yaml";
import { ComponentTypes } from "@apa/core-renderers";
import type { AdminPage } from "./types";
import { normalizeComponents } from "./pageEditorUtils";

/**
 * Editable Markdown format:
 * - Frontmatter: --- slug, language, title, description ---
 * - Body: ## Section, ::: ComponentType ... :::, or ```ArticleHero ... ``` (fenced)
 * - Inside blocks, content as YAML (no key; keys are runtime-only)
 */

function escapeFrontmatterValue(s: string): string {
	if (/^[a-z0-9_-]+$/i.test(s)) return s;
	return JSON.stringify(s);
}

/** Build YAML body for ArticleHero fenced block. Uses image shorthand, omits empty optionals. */
function stringifyArticleHeroContent(content: Record<string, unknown>): string {
	const {
		shareLabel: _sl,
		shareOptions: _so,
		featuredImage,
		...rest
	} = content;

	// Use image: src | alt shorthand when featuredImage is simple { src, alt }
	const img = featuredImage as { src?: unknown; alt?: string; options?: unknown } | undefined;
	const useImageShorthand =
		img &&
		typeof img === "object" &&
		img.src != null &&
		typeof img.src === "string" &&
		!img.options;
	const alt = (img?.alt ?? "Image") as string;

	const body: Record<string, unknown> = { ...rest };
	if (useImageShorthand) {
		body.image = `${img.src} | ${alt}`;
	} else if (featuredImage) {
		body.featuredImage = featuredImage;
	}

	// Omit empty optional string fields
	const omitIfEmpty = [
		"eyebrow",
		"summary",
		"author",
		"authorRole",
		"publishDate",
		"breadcrumbsLink",
		"breadcrumbsCategoryPath"
	] as const;
	for (const key of omitIfEmpty) {
		if (body[key] === "" || body[key] == null) delete body[key];
	}
	if (Array.isArray(body.breadcrumbsCategoryPathIds) && body.breadcrumbsCategoryPathIds.length === 0) {
		delete body.breadcrumbsCategoryPathIds;
	}
	if (body.breadcrumbsLink && typeof body.breadcrumbsLink === "object") {
		const link = body.breadcrumbsLink as { slug?: string; external?: string } | null;
		const slug = link?.slug?.toString().trim();
		const external = link?.external?.toString().trim();
		if (!slug && !external) {
			delete body.breadcrumbsLink;
		}
	}

	return stringify(body, { indent: 2 }).trim();
}

export function pageToEditableMarkdown(
	draft: AdminPage | null,
	fallbackLanguage: string,
	options?: { includeFrontmatter?: boolean }
): string {
	if (!draft) return "";

	const components = normalizeComponents(draft.components);
	const language = (draft.language ?? fallbackLanguage).trim() || fallbackLanguage;
	const slug = (draft.slug ?? "").trim();
	const title = (draft.title ?? "").trim();
	const description = (draft.metadata?.description ?? "").trim();

	const lines: string[] = [];
	if (options?.includeFrontmatter !== false) {
		lines.push(
			"---",
			`slug: ${escapeFrontmatterValue(slug)}`,
			`language: ${escapeFrontmatterValue(language)}`,
			`title: ${escapeFrontmatterValue(title)}`,
			`description: ${escapeFrontmatterValue(description)}`,
			"---",
			""
		);
	}

	for (const comp of components) {
		const type = (comp.componentType ?? "").toString();
		const rawContent = comp.content ?? {};
		const content = typeof rawContent === "object" && rawContent !== null && !Array.isArray(rawContent)
			? (() => { const { key: _k, ...rest } = rawContent as Record<string, unknown>; return rest; })()
			: rawContent;

		// PageHeader → # title at this position (not ::: block)
		if (type === ComponentTypes.PageHeader) {
			const headerTitle =
				content && typeof content === "object"
					? String((content as { title?: string }).title ?? "").trim()
					: "";
			if (headerTitle && headerTitle !== "/") {
				lines.push(`# ${headerTitle}`, "");
			}
			continue;
		}

		if (type === ComponentTypes.SectionHeading) {
			const text = (content as { text?: string }).text ?? "";
			const singleLine = text.replace(/\r?\n/g, " ").trim() || "Section";
			lines.push(`## ${singleLine}`, "");
			continue;
		}

		if (type === ComponentTypes.TextBlock) {
			const paragraphs = (content as { paragraphs?: string[] }).paragraphs ?? [];
			// Output as raw markdown: one paragraph per block, blank line between (markdown experience)
			const trimmed = paragraphs.map((p) => String(p ?? "").trim()).filter(Boolean);
			for (let idx = 0; idx < trimmed.length; idx++) {
				lines.push(trimmed[idx]);
				if (idx < trimmed.length - 1) lines.push("");
			}
			if (trimmed.length > 0) lines.push("");
			continue;
		}

		// ArticleHero: use fenced block format (```ArticleHero ... ```)
		if (type === ComponentTypes.ArticleHero) {
			const yamlBody = stringifyArticleHeroContent(content as Record<string, unknown>);
			lines.push("```ArticleHero");
			lines.push(yamlBody);
			lines.push("```", "");
			continue;
		}

		// ::: ComponentType ... ::: (no key in body)
		let blockBody: Record<string, unknown> = typeof content === "object" && content !== null ? (content as Record<string, unknown>) : {};
		const yamlBody = stringify(blockBody, { indent: 2 }).trim();
		lines.push(`::: ${type}`);
		lines.push(yamlBody);
		lines.push(":::", "");
	}

	return lines.join("\n").trimEnd();
}
